import React, { Fragment } from 'react';
import { compose } from 'recompose';
import { withLocale } from '../hocs/withLocale';
import { Button, Snackbar, SnackbarContent, withStyles, WithStyles } from '@material-ui/core';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import Cookies from 'universal-cookie';

// Define the styles type
interface CookiesPolicyStyles {
  root: React.CSSProperties;
  day: React.CSSProperties;
  footer: React.CSSProperties;
  buttons: React.CSSProperties;
  button: React.CSSProperties;
  eventGroup: React.CSSProperties;
  groupNames: React.CSSProperties;
  groupName: React.CSSProperties;
}

// Define props interface
interface CookiesPolicyProps extends WithStyles<keyof CookiesPolicyStyles>, WithSnackbarProps {
  translate: (key: string) => string;
}

// Define state interface
interface CookiesPolicyState {
  show: boolean;
}

// Define the styles
const styles = (theme: any) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  day: {
    padding: theme.spacing(1),
    flex: '1 0 auto',
  },
  footer: {
    padding: `${theme.spacing(1)}px 0`,
    zIndex: 1,
  },
  buttons: {
    '& > *': {
      flex: 1,
    },
  },
  button: {
    margin: `0 ${theme.spacing(1)}px`,
  },
  eventGroup: {
    flex: 1,
  },
  groupNames: {
    marginTop: theme.spacing(1),
  },
  groupName: {
    overflow: 'hidden',
    padding: theme.spacing(1),
  },
});

// Create the component class with typed props and state
class CookiesPolicyComponent extends React.Component<CookiesPolicyProps, CookiesPolicyState> {
  constructor(props: CookiesPolicyProps) {
    super(props);
    const cookies = new Cookies();
    const cookiesPolicyAccepted = cookies.get('cookiesPolicyAccepted');
    this.state = {
      show: cookiesPolicyAccepted !== 'true',
    };
  }

  handleAcceptAll = () => {
    this.saveConsent({
      analytics: true,
      functional: true,
      marketing: true, // Включаємо маркетинг
    });
  };

  handleAcceptNecessary = () => {
    this.saveConsent({
      analytics: true,
      functional: true,
      marketing: false, // Тільки необхідні
    });
  };

  saveConsent = (preferences: { analytics: boolean; functional: boolean; marketing: boolean }) => {
    const cookies = new Cookies();
    const current = new Date();
    const nextYear = new Date();
    nextYear.setFullYear(current.getFullYear() + 1);

    cookies.set('cookiesPolicyAccepted', true, {
      path: '/',
      expires: nextYear,
    });

    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };

    try {
      localStorage.setItem('voopty_consent_preferences', JSON.stringify(consent));

      window.dispatchEvent(
        new CustomEvent('consentChanged', {
          detail: consent,
        })
      );

      console.log('🍪 User consent saved:', consent);

      this.initializeAnalytics();
    } catch (error) {
      console.error('Error saving consent:', error);
    }

    this.setState({ show: false });
  };

  initializeAnalytics = () => {
    try {
      if (typeof (window as any).initializeAnalyticsAfterConsent === 'function') {
        (window as any).initializeAnalyticsAfterConsent();
        console.log('✅ Analytics initialized via global function');
        return;
      }

      window.dispatchEvent(
        new CustomEvent('initializeAnalytics', {
          detail: { type: 'student', consent: true },
        })
      );

      console.log('✅ Analytics initialization event dispatched');
    } catch (error) {
      console.error('Error initializing analytics:', error);
    }
  };

  render = (): React.ReactNode => {
    return (
      <Snackbar
        style={{ zIndex: 100000 }}
        onClose={() => {
          this.setState({ show: false });
        }}
        open={this.state.show}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <SnackbarContent
          action={
            <Fragment>
              <Button onClick={this.handleAcceptAll} color="primary" variant="contained" style={{ marginRight: 8 }}>
                {this.props.translate('cookiesPolicy.acceptAll')}
              </Button>
              <Button onClick={this.handleAcceptNecessary} color="default" variant="outlined">
                {this.props.translate('cookiesPolicy.acceptNecessary')}
              </Button>
            </Fragment>
          }
          message={this.props.translate('cookiesPolicy.text')}
        />
      </Snackbar>
    );
  };
}

// Export the composed component
export const CookiesPolicy = compose<CookiesPolicyProps, {}>(
  withStyles(styles),
  withLocale,
  withSnackbar
)(CookiesPolicyComponent);
